import React from 'react';
import { AlertTriangle, Flame, Car, Activity, AlertCircle, HelpCircle, Check, Lock } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

const INCIDENT_TYPES = [
  { type: 'crime', label: 'Criminal Activity', icon: AlertTriangle, color: '#ef4444', free: true },
  { type: 'fire', label: 'Fire/Rescue', icon: Flame, color: '#f97316', free: true },
  { type: 'traffic', label: 'Traffic', icon: Car, color: '#eab308', free: true },
  { type: 'medical', label: 'Medical', icon: Activity, color: '#3b82f6', free: false },
  { type: 'hazard', label: 'Hazards', icon: AlertCircle, color: '#a855f7', free: false },
  { type: 'other', label: 'Other', icon: HelpCircle, color: '#6b7280', free: false },
];

const FilterPanel = ({ activeFilters = [], onFilterChange, userTier = 'free' }) => {
  const isPaidUser = userTier !== 'free';

  const handleToggle = (type, isAvailable) => {
    if (!isAvailable) return;
    
    if (activeFilters.includes(type)) {
      onFilterChange(activeFilters.filter(f => f !== type));
    } else {
      onFilterChange([...activeFilters, type]);
    }
  };

  const selectAll = () => {
    const available = INCIDENT_TYPES
      .filter(t => t.free || isPaidUser)
      .map(t => t.type);
    onFilterChange(available);
  };

  const clearAll = () => {
    onFilterChange([]);
  };

  return (
    <div className="p-4" data-testid="filter-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
          Incident Filters
        </h3>
        <div className="flex gap-2">
          <button 
            onClick={selectAll}
            className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
            data-testid="select-all-filters"
          >
            All
          </button>
          <span className="text-slate-600">|</span>
          <button 
            onClick={clearAll}
            className="text-xs text-slate-400 hover:text-slate-300 transition-colors"
            data-testid="clear-all-filters"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {INCIDENT_TYPES.map((item) => {
          const Icon = item.icon;
          const isAvailable = item.free || isPaidUser;
          const isActive = activeFilters.includes(item.type);
          
          return (
            <div 
              key={item.type}
              className={`flex items-center justify-between p-2 rounded transition-colors ${
                isAvailable 
                  ? 'hover:bg-slate-800/50 cursor-pointer' 
                  : 'opacity-50 cursor-not-allowed'
              } ${isActive ? 'bg-slate-800/70' : ''}`}
              onClick={() => handleToggle(item.type, isAvailable)}
              data-testid={`filter-${item.type}`}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded flex items-center justify-center"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  <Icon className="w-4 h-4" style={{ color: item.color }} />
                </div>
                <Label className={`text-sm ${isAvailable ? 'text-slate-200' : 'text-slate-500'}`}>
                  {item.label}
                </Label>
              </div>
              
              {isAvailable ? (
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  isActive 
                    ? 'bg-teal-500 border-teal-500' 
                    : 'border-slate-600 hover:border-slate-500'
                }`}>
                  {isActive && <Check className="w-3 h-3 text-white" />}
                </div>
              ) : (
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Lock className="w-3 h-3" />
                  <span>PRO</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isPaidUser && (
        <>
          <Separator className="my-4 bg-slate-800" />
          <div className="text-center">
            <p className="text-xs text-slate-500 mb-2">
              Unlock all filters with a paid plan
            </p>
            <a 
              href="/subscription" 
              className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
            >
              Upgrade Now →
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default FilterPanel;
